import { useTranslations } from '@vocab/react';
import { Text } from 'braid-design-system';
import { SkeletonLoader } from '../../../shared/components/SkeletonLoader';
import type { MonthPickerValue } from '../../type';
import translations from './.vocab';

export const WorkHistoryDetails = ({
  workHistoryFrom,
  workHistoryTo,
  isStillWorkHere,
}: {
  isStillWorkHere: boolean | undefined;
  workHistoryFrom: MonthPickerValue | undefined;
  workHistoryTo: MonthPickerValue | undefined;
}) => {
  const { t } = useTranslations(translations);

  const noWorkHistoryDetails =
    !workHistoryFrom?.month &&
    !workHistoryFrom?.year &&
    !workHistoryTo?.month &&
    !workHistoryTo?.year;

  const invalidWorkHistoryDetails =
    !isStillWorkHere &&
    workHistoryFrom?.year &&
    workHistoryTo?.year &&
    workHistoryFrom?.year > workHistoryTo?.year;

  const shouldShowWorkHistoryDetails = Boolean(
    !invalidWorkHistoryDetails &&
      ((workHistoryFrom?.month &&
        workHistoryFrom.year &&
        workHistoryTo?.month &&
        workHistoryTo.year) ||
        (workHistoryFrom?.month && workHistoryFrom.year && isStillWorkHere)),
  );

  const shouldShowLoaderForWorkHistoryDetails = Boolean(
    (!noWorkHistoryDetails && !shouldShowWorkHistoryDetails) ||
      invalidWorkHistoryDetails,
  );

  const yearsWorked = (
    yearFrom: number,
    yearTo: number | undefined,
    isStillWorkHere: boolean | undefined,
  ) => {
    let yearsSpentInRole;
    if (yearFrom === yearTo || new Date().getFullYear() === yearFrom) {
      yearsSpentInRole = t('Less than 1 year in the role, ');
    } else if (yearTo && !isStillWorkHere) {
      yearsSpentInRole = t('{start} to {end} years in the role, ', {
        start: String(yearTo - yearFrom),
        end: String(yearTo - yearFrom + 1),
      });
    } else if (yearFrom && isStillWorkHere) {
      yearsSpentInRole = t('More than {years} years in the role, ', {
        years: String(new Date().getFullYear() - yearFrom),
      });
    }

    const currentOrFormerString = isStillWorkHere
      ? t('current employee')
      : t('former employee');

    return yearsSpentInRole + currentOrFormerString;
  };

  return (
    <>
      {noWorkHistoryDetails && (
        <Text tone="secondary">
          <i>{t('Time spent in the role')}</i>
        </Text>
      )}
      {shouldShowLoaderForWorkHistoryDetails && <SkeletonLoader />}
      {shouldShowWorkHistoryDetails && (
        <Text>
          {workHistoryFrom?.year &&
            yearsWorked(
              workHistoryFrom.year,
              workHistoryTo?.year,
              isStillWorkHere,
            )}
        </Text>
      )}
    </>
  );
};
